import type { DocumentRecord, FarmDetail, FarmerDetail, HarvestDelivery, InputDistribution } from "@/types/domain";

type ListResponse<T> = { items: T[] };

export interface FarmerProfileServices {
  farmers: { get(id: string): Promise<FarmerDetail | undefined> };
  farms: { listForFarmer(farmerId: string): Promise<ListResponse<FarmDetail>> };
  inputs: { listDistributions(): Promise<ListResponse<InputDistribution>> };
  harvest: { list(): Promise<ListResponse<HarvestDelivery>> };
  media: { list(): Promise<ListResponse<DocumentRecord>> };
}

export interface FarmerProfileLoadResult {
  farmer: FarmerDetail;
  linkedFarms: FarmDetail[];
  distributions: InputDistribution[];
  harvests: HarvestDelivery[];
  documents: DocumentRecord[];
  warnings: string[];
}

export async function loadFarmerProfile(services: FarmerProfileServices, farmerId: string): Promise<FarmerProfileLoadResult> {
  const farmer = await services.farmers.get(farmerId);
  if (!farmer) throw new Error("Farmer record was not found.");

  const [farmResult, distributionResult, harvestResult, documentResult] = await Promise.allSettled([
    services.farms.listForFarmer(farmerId),
    services.inputs.listDistributions(),
    services.harvest.list(),
    services.media.list(),
  ]);

  const warnings: string[] = [];
  const linkedFarms = farmResult.status === "fulfilled"
    ? farmResult.value.items
    : (warnings.push("farms"), []);
  const distributions = distributionResult.status === "fulfilled"
    ? distributionResult.value.items.filter((item) => item.farmerId === farmerId)
    : (warnings.push("input distributions"), []);
  const harvests = harvestResult.status === "fulfilled"
    ? harvestResult.value.items.filter((item) => item.farmerId === farmerId)
    : (warnings.push("harvest history"), []);
  const linkedEntityIds = new Set([farmerId, ...linkedFarms.map((farm) => farm.id)]);
  const documents = documentResult.status === "fulfilled"
    ? documentResult.value.items.filter((document) => linkedEntityIds.has(document.entityId))
    : (warnings.push("documents and media"), []);

  return { farmer, linkedFarms, distributions, harvests, documents, warnings };
}
